import { Component, h, Host, Listen, Method, Prop, Element, Watch } from '@stencil/core';

export type HyloMenuItem = {
  name: string;
  slug: string;
  id: string;
  href: string;
  active: boolean;
  ancestor: boolean;
  sibling: boolean;
  descendant: boolean;
  children?: HyloMenuItem[];
}

export type HyloMenu = {
  [key: string]: HyloMenuItem[] | HyloMenuItem | string;
};

export type HyloMenuAccessor = {
  data: HyloMenu;
  getPage: (pageIds: string[]) => HyloMenuItem;
  getPageHref: (pageOrPageIds: string[] | HyloMenuItem, options?: HyloMenuPageHrefOptions) => string;
};

export interface HyloMenuPageHrefOptions {
  uuid?: string;
  stripQuery?: boolean;
}

/**
 * A general content root component.
 * It wraps the whole site. Every other structure component goes inside this component like hylo-app, hylo-header and hylo-footer
 * but also things like hylo-router and hylo-nav. A better name would have been hylo-site-root but for the sake of
 * backwards compatibility it's to late for that.
 *
 * Adds ionic style classes, a general page structure and basic page functionality.
 *
 * If provided, many hylo WebComponents look for translations of their own text, labels and announcement properties
 * in the translation object.
 *
 * Many hylo WebComponents use the announce functionality of this component to handle their own announcements.
 *
 * Sets the focus to the site start/top on every page change.
 * A hylo-nav WebComponent might reset the focus when a previously loaded page gets revisited.
 *
 * Add the attribute 'style="visibility: hidden"' to reduce content jumping while initial page loading.
 */



@Component({
  tag: 'hylo-page-root',
  styleUrl: 'hylo-page-root.scss',
})
export class HyloPageRoot {


  /** @internal */
  constructor() {

    this.onLoaderChange = this.onLoaderChange.bind(this);
  }


  /**
   * An element which marks the beginning of the main content.
   * The user will be able to jump directly from the beginning of the root content to this element and skip the header.
   * The element should be focusable.
   *
   * Provide a css query or a HTMLElement.
   */
  @Prop() mainContentStart: string | HTMLElement;

  /**
   * Adds a hylo-scroll-top component to the end of the root content but before the "jump to page start" marker and "appendix" slot.
   */
  @Prop() showScrollTop: boolean;

  /**
   * A hylo-data WebComponent which includes translated text.
   *
   * This component looks in the hylo-data WebComponent for the text, label and announcement properties of this component.
   * If found (as key), it uses the value instead of the value of the property.
   *
   * Accepts a css query string or a HTMLHyloDataElement.
   */
  @Prop() translationData: string | HTMLHyloDataElement;

  /**
   * If "translationData" was provided, the returned Promise resolved with the object of the HTMLHyloDataElement
   * defined by "translationData". The returned promise resolves when the object got populated.
   *
   * If no "translationData" was provided, it returns null.
   *
   * Meant to be used only as getter. Don't set this property.
   *
   * Replaces "getTranslations()" which does not resolve before this component is fully initiated.
   */
  @Prop() translationPromise: Promise<any> = new Promise((resolve) => {this.translation_resolve = resolve});

  /**
   * Delays the resolving of the translationPromise until this value is "false".
   */
  @Prop() translationPromiseDelay: boolean;

  /**
    * A hylo-data WebComponent which includes the menu data or its id.
    */
  @Prop() menuData: string | HTMLHyloDataElement;

  /**
   * If "menuData" was provided, the returned Promise resolved with the object of the HTMLHyloDataElement
   * defined by "menuData". The returned promise resolves when the object got populated.
   *
   * If no "menuData" was provided, it returns null.
   *
   * Meant to be used only as getter. Don't set this property.
   */
  @Prop() menuPromise: Promise<HyloMenuAccessor> = new Promise((resolve) => {this.menu_resolve = resolve});

  /**
   * Delays the resolving of the menuPromise until this value is "false".
   */
  @Prop() menuPromiseDelay: boolean;

  /**
   * The id the included hylo-accessibility-announcement element gets.
   */
  @Prop() announcementId: string = 'announcement';


  @Prop() labelPageStart: string = 'Seitenanfang';
  @Prop() labelPageEnd: string = 'Seitenende';
  @Prop() labelJumpmarkToPageStart: string = 'Springe zum Seitenanfang';
  @Prop() textJumpToMainContent: string = 'Springe zum Hauptinhalt';
  @Prop() textJumpToPageStart: string = 'Springe zum Seitenanfang';
  @Prop() announcementPageIsChanging: string = 'Die Seite wird geladen. Bitte warten';
  @Prop() announcementPageChanged: string = 'Die Seite wurde geladen';


  /**
   * Returns the included HTMLHyloLoaderElement WebComponent.
   */
  @Method()
  async getLoader() {

    return this.loader;
  }

  /**
   * If "translationData" was provided, it returns the object of the HTMLHyloDataElement defined by "translationData".
   * The returned promise resolves when the object got populated.
   *
   * If no "translationData" was provided, it returns null.
   *
   * @deprecated Use the property translationPromise instead.
   */
  @Method()
  getTranslations(): Promise<any> {

    return this.translationPromise;
  }

  /**
   * Returns the included hylo-accessibility-announcement element.
   */
  @Method()
  async getAnnouncement() {

    return this.announcement;
  }

  /**
   * Announces the given text by using the included hylo-accessibility-announcement element.
   */
  @Method()
  async announce(text: string) {

    if (!this.announcement) {
      window.setTimeout(() => {
        this.announce(text);
      }, 100);
      return;
    }

    this.announcement.setAnnouncement(text);
  }


  @Element() el: HTMLHyloPageRootElement;

  private me: HTMLElement;
  private loader: HTMLHyloLoaderElement;
  private legacy: boolean;

  private start: HTMLElement;
  private start_aside: HTMLElement;
  private end: HTMLElement;
  private end_aside: HTMLElement;
  private jump_main: HTMLElement;
  private jump_start: HTMLElement;
  private jump_start_aside: HTMLElement;
  private announcement: HTMLHyloAccessibilityAnnouncementElement;

  private translation: HTMLHyloDataElement;
  private translation_object: any = {};
  private translation_resolve: Function;
  private translation_checked: boolean;

  private menu: HTMLHyloDataElement;
  private menu_resolve: Function;
  private menu_checked: boolean;

  private page_changing: boolean;


  /** @internal */
  @Watch('translationPromiseDelay')
  translationPromiseDelayChanged() {

    this.checkTranslation();
  }

  /** @internal */
  @Watch('menuPromiseDelay')
  menuPromiseDelayChanged() {

    this.checkMenu();
  }


  /** @internal */
  @Listen('hyloNavWillChange')
  onHyloNavWillChange() {

    this.loader.turnUp();
    this.page_changing = true;
    this.announcement.setAnnouncement((this.translation_object['announcementPageIsChanging'] ? this.translation_object['announcementPageIsChanging'] : this.announcementPageIsChanging) + ',');
  }

  /** @internal */
  @Listen('hyloNavDidChange')
  onHyloNavDidChange() {

    this.start.focus();
    this.loader.turnDown();
  }

  /*@Listen('focusin')
  onFocusin(event: FocusEvent) {

    console.log(event.target);
  }*/


  /** @internal */
  componentWillLoad() {

    this.legacy = document.body.classList.contains('hylo-all-legacy');
    if (!this.legacy) this.legacy = document.body.classList.contains('hylo-page-root-legacy');

    this.checkTranslation();
    this.checkMenu();
  }

  /** @internal */
  componentDidLoad() {

    this.setTranslation();
    this.me.style.visibility = 'visible';

    this.loader.addEventListener('hyloLoaderChange', this.onLoaderChange);

    if (this.loader.on) this.page_changing = true;
    else {
      this.announcement.setAnnouncement((this.translation_object['announcementPageChanged'] ? this.translation_object['announcementPageChanged'] : this.announcementPageChanged) + ',');
    }
  }


  private checkTranslation() {

    if (this.translationPromiseDelay || this.translation_checked) return;
    this.translation_checked = true;

    if (this.translationData) {
      if (typeof this.translationData == 'string') {
        this.translation = document.querySelector(this.translationData) as HTMLHyloDataElement;
      } else this.translation = this.translationData;
    } else {
      this.translation_resolve(null);
    }

    if (this.translation) {
      if (this.translation.object) {
        this.translation_resolve(this.translation.object);
        this.setTranslation();
      } else this.translation.getObject().then(() => {
        this.translation_resolve(this.translation.object);
        this.setTranslation();
      });
    }
  }

  private setTranslation() {

    if (!this.jump_main || !this.translation || !this.translation.object || Object.keys(this.translation_object).length) return;
    const translation = this.translation.object;

    if (translation.labelPageStart) {
      this.start_aside.setAttribute('aria-label', translation.labelPageStart+',');
      this.start.innerHTML = translation.labelPageStart+',';
    }
    if (translation.textJumpToMainContent) this.jump_main.innerHTML = translation.textJumpToMainContent+'.';
    if (translation.labelJumpmarkToPageStart) this.jump_start_aside.setAttribute('aria-label', translation.labelJumpmarkToPageStart+',');
    if (translation.textJumpToPageStart) this.jump_start.innerHTML = translation.textJumpToPageStart+'.';
    if (translation.labelPageEnd) {
      this.end_aside.setAttribute('aria-label', translation.labelPageEnd+',');
      this.end.innerHTML = translation.labelPageEnd+',';
    }

    this.translation_object = translation;
  }

  private checkMenu() {

    if (this.menuPromiseDelay || this.menu_checked) return;
    this.menu_checked = true;


    if (this.menuData) {
      if (typeof this.menuData == 'string') {
        this.menu = document.querySelector(this.menuData) as HTMLHyloDataElement;
      } else {
        this.menu = this.menuData;
      }
    } else {
      this.menu_resolve(null);
    }

    if (this.menu) {
      if (this.menu.object) {
        const mainMenu = this.menu.object as HyloMenu;
        if (mainMenu) {
          this.menu_resolve(this.getMenuAccessor(mainMenu));
        }
      } else 
        this.menu.getObject().then(() => {
          const mainMenu = this.menu.object as HyloMenu;
          if (mainMenu) {
            this.menu_resolve(this.getMenuAccessor(mainMenu));
          }
        });
    }
  }

  private getMenuAccessor(menu: HyloMenu): HyloMenuAccessor {
    return {
      data: menu,
      getPage: (pageIds: string[]) => {
        return this.getPage(menu, pageIds);
      },
      getPageHref: (pageOrPageIds: string[] | HyloMenuItem, options?: HyloMenuPageHrefOptions) => {
        return this.getPageHref(menu, pageOrPageIds, options);
      },
    }
  }
  
  private getPage(menu: HyloMenu, pageIds: string[]): HyloMenuItem | null {
    if (pageIds.length === 0) {
      return null; // Return null if no page IDs are provided
    }
  
    let currentItem: HyloMenuItem[] | HyloMenuItem | string = menu[pageIds[0]]; // Temporarily use 'any' to bypass type checking
  
    for (let i = 1; i < pageIds.length; i++) {
      if (Array.isArray(currentItem)) {
        currentItem = currentItem.find(item => (item.id === pageIds[i]) || (item.slug === pageIds[i]));
      } else if (currentItem && typeof currentItem === 'object' && 'children' in currentItem) {
        currentItem = currentItem.children.find(item => (item.id === pageIds[i]) || (item.slug === pageIds[i]));
      } else {
        return null; // Return null if current item is not a MenuItem or MenuItem[]
      }
  
      if (!currentItem) {
        return null; // No menu item found for the given ID
      }
    }
  
    return typeof currentItem === 'object' && !Array.isArray(currentItem) ? currentItem : null;
  }
  
  private getPageHref(
    menu: HyloMenu,
    pageOrPageIds: string[] | HyloMenuItem,
    options: HyloMenuPageHrefOptions = { stripQuery: false },
  ): string {
    let href = '';
  
    let menuItem: HyloMenuItem;
    
    if (Array.isArray(pageOrPageIds) && pageOrPageIds.length > 0) {
      menuItem = this.getPage(menu, pageOrPageIds);
    } else {
      menuItem = pageOrPageIds as HyloMenuItem;
    }
  
    if (!menuItem) return '';
  
    href = menuItem.href;
  
    if (options.uuid) {
      const [baseHref, query] = href.split('?');
      const newBaseHref = baseHref + options.uuid;
      href = query ? `${newBaseHref}?${query}` : newBaseHref;
    }
  
    if (options.stripQuery) {
      const [baseHref] = href.split('?');
      return baseHref;
    }
  
    return href;
  }

  private onLoaderChange(event: CustomEvent) {

    if (!this.page_changing) return;
    if (event.detail) return;

    this.page_changing = false;
    this.announcement.setAnnouncement((this.translation_object['announcementPageChanged'] ? this.translation_object['announcementPageChanged'] : this.announcementPageChanged) + ',');
  }



  /** @internal */
  render() {

    return (
      <Host ref={el => this.me = el} style={{visibility: 'hidden'}}>
        {this.legacy && <hylo-page-root-legacy/>}
        <aside ref={el => this.start_aside = el} aria-label={`${this.labelPageStart},`}>
          <hylo-sr-only
            ref={el => this.start = el}
            id="hylo_root_page_start"
            tabindex="-1"
          >{this.labelPageStart},</hylo-sr-only>
          <hylo-skip-to
            ref={el => this.jump_main = el}
            id="hylo_root_skip_to_main"
            goto={this.mainContentStart}
            focusDelay={500}
          >{this.textJumpToMainContent}.</hylo-skip-to>
        </aside>

        <slot/>

        <aside
          ref={el => this.jump_start_aside = el}
          class="hidden-on-layer"
          aria-label={`${this.labelJumpmarkToPageStart},`}
        >
          {this.showScrollTop && <hylo-scroll-top></hylo-scroll-top>}
          <hylo-skip-to
            ref={el => this.jump_start = el}
            id="hylo_root_skip-to_page_start"
            goto={'#hylo_root_page_start'}
          >{this.textJumpToPageStart}.</hylo-skip-to>
        </aside>

        <slot name="appendix" />

        <hylo-accessibility-announcement id={this.announcementId} ref={el => this.announcement = el}></hylo-accessibility-announcement>

        <aside
          ref={el => this.end_aside = el}
          id="hylo_root_page_end"
          aria-label={`${this.labelPageEnd},`}
        >
          <hylo-sr-only ref={el => this.end = el}>{this.labelPageEnd},</hylo-sr-only>
        </aside>

        <hylo-loader ref={el => this.loader = el}/>
      </Host>
    );
  }
}
